import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup"; // Import Yup for validation
import axiosInstance from "../../utils/axiosInstance";
import DynamicField from "./DynamicFields"; // Reusable DynamicField Component
import { toast } from "react-toastify";

const DynamicForm = ({ ticketId , status}) => {
  const [formConfig, setFormConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialValues, setInitialValues] = useState({}); // Store initial values of the form fields
  const [formName, setFormName] = useState("");

    // Fetch form configuration from your API
    const fetchFormConfig = async () => {
      try {
        const response = await axiosInstance.get(
          `workflow/completeform/${ticketId}?status=${status}` // Pass ticketId dynamically
        );

        const formFields =
          response.data.data.workflowPipeline.formBuilder.fields;
        const formName = response.data.data.name;
        setFormConfig(formFields); // Store the form fields
        setFormName(formName);

        // Populate initialValues, handling nested keys like "formInfo.amount"
        const initialValues = formFields.reduce((acc, field) => {
          const keyPath = field.fieldId.key.split("."); // Split nested keys
          const value = field?.fieldId?.value || "";

          // Set value in nested structure
          let current = acc;
          for (let i = 0; i < keyPath.length; i++) {
            const key = keyPath[i];
            if (i === keyPath.length - 1) {
              current[key] = value; // Assign value at the last key
            } else {
              current[key] = current[key] || {}; // Ensure object exists
              current = current[key];
            }
          }

          return acc;
        }, {});

        setInitialValues(initialValues); // Set initial values for the form fields
        setLoading(false); // Set loading to false after the data is fetched
      } catch (err) {
        setError("Failed to load form configuration"); // Handle error
        setLoading(false); // Set loading to false if there's an error
      }
    };


  useEffect(() => {
    if (ticketId && status) {
      fetchFormConfig();
    }
  }, [ticketId, status]); // Re-fetch when ticketId or status changes

  // Dynamically generate Yup validation schema
  const validationSchema = Yup.object().shape(
    formConfig?.reduce((acc, field) => {
      if (field.fieldId.required) {
        // Check if the field value should be treated as a string or mixed type
        const validation = Yup.mixed().required(
          `${field.fieldId.label} is required`
        );

        // If it's a string, apply trim
        if (field.fieldId.type === "string") {
          acc[field.fieldId.key] = validation.trim();
        } else {
          acc[field.fieldId.key] = validation;
        }
      }
      return acc;
    }, {})
  );

  const getModifiedFields = (initialValues, submittedValues) => {
    const result = {};

    for (let key in submittedValues) {
      if (submittedValues.hasOwnProperty(key)) {
        const submittedValue = submittedValues[key];
        const initialValue = initialValues[key];

        if (submittedValue && typeof submittedValue === "object") {
          if ("value" in submittedValue && "label" in submittedValue) {
            // If it's a dropdown field, include only the `value`
            if (submittedValue.value !== initialValue?.value) {
              result[key] = submittedValue.value; // Send only `value`
            }
          } else if (Array.isArray(submittedValue)) {
            // Handle file arrays - include all file keys
            if (JSON.stringify(submittedValue) !== JSON.stringify(initialValue)) {
              result[key] = submittedValue;
            }
          } else {
            // Recursively process nested objects
            const nestedFields = getModifiedFields(
              initialValue || {},
              submittedValue
            );
            if (Object.keys(nestedFields).length > 0) {
              result[key] = nestedFields;
            }
          }
        } else if (
          submittedValue !== initialValue && // Field value is modified
          (submittedValue !== "" || initialValue === undefined) // Either field is newly filled or explicitly set
        ) {
          result[key] = submittedValue;
        }
      }
    }

    return result;
  };

  const handleSubmit = async (submittedValues) => {
    // Extract modified fields
    const filteredPayload = getModifiedFields(initialValues, submittedValues);

    // Add ticket ID to the payload
    filteredPayload.ticketId = ticketId;

    try {
      // Send API call to update form data
      const response = await axiosInstance.put(
        "ticket/updateform",
        filteredPayload
      );
      toast.success(response.data.message); // Show success message2222
    } catch (error) {
      console.error("Error updating form:", error);
      toast.error("Failed to update form"); // Show error message
    }
  };
  function mergeFieldsByLabel(fieldsArray) {
    const grouped = {};
  
    fieldsArray?.forEach(({ fieldId, order, _id }) => {
      const label = fieldId.label;
  
      if (!grouped[label]) {
        // Initialize the group for this label
        grouped[label] = {
          ...fieldId,
          _id,
          order,
          fields: [...fieldId.fields],
          value: Array.isArray(fieldId.value) ? [...fieldId.value] : [],
        };
      } else {
        if (fieldId.type === "object") {
          grouped[label].fields = [
            ...grouped[label].fields,
            { type: "separator" },
            ...fieldId.fields,
          ];
        } else {
          grouped[label].fields = [...grouped[label].fields, ...fieldId.fields];
        }
  
        grouped[label].value = [
          ...grouped[label].value,
          ...(Array.isArray(fieldId?.value) ? fieldId.value : []),
        ];
      }
    });
  
    return Object.values(grouped);
  }
  
  const mergedFormConfig = mergeFieldsByLabel(formConfig);


  return (
    <div className="DynamicFormContainer">
      <h5>{formName}</h5> 
      {loading ? (
        <p>Loading form...</p>
      ) : error ? (
        <p>{error}</p>
      ) : mergedFormConfig.length > 0 ? (
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          // validationSchema={validationSchema}  // Dynamically generated validation schema
        >
          {({ errors, touched, setFieldValue , dirty}) => (
            <Form>
              <div className="row">
                {mergedFormConfig.map((field) => (
                  <div
                    className={field.type === "object" ? "col-md-12" : "col-md-6 SingleFieldBlock"}
                    key={field.key}
                  >

                    <DynamicField field={field} setFieldValue={setFieldValue} />


                    {/* Display error message below the field */}
                    {/* {errors[field.fieldId.key] && touched[field.fieldId.key] && (
                      <div className="text-danger">{errors[field.fieldId.key]}</div>
                    )} */}
                  </div>
                ))}
                {/* <div className="col-md-12">
                  <button type="submit" className="btn btn-primary">Submit</button>
                </div> */}
                <button
                  className="btn btn-dark btn_dark ms-3 DynamicFormSubmit"
                  type="submit"
                  disabled={!dirty}
                >
                  Submit
                </button>
              </div>
            </Form>
          )}
        </Formik>
      ) : (
        <p>No form fields available</p>
      )}
    </div>
  );
};

export default DynamicForm;
